import { Analytics } from '@/Telemetry';
import { deleteCookie, getCookie, setCookie } from '@/Utility/cookie';
import { integrationsSyncMutation } from '@/graphql/mutations/TelemetrySync.mutation';
import { hubspotUserTokenSyncMutationCompat } from '@/graphql/mutations/TelemetrySyncCompat.mutation';

/**
 * TelemetrySyncSession
 * @description - Singleton class to manage the telemetry sync during the session
 */
class TelemetrySyncSession {
  private static instance: TelemetrySyncSession;

  private constructor() {}

  public static getInstance(): TelemetrySyncSession {
    if (!TelemetrySyncSession.instance) {
      TelemetrySyncSession.instance = new TelemetrySyncSession();
    }

    return TelemetrySyncSession.instance;
  }

  public syncIntegrations() {
    this.syncHubspot();
    this.syncMarketo();
  }

  private getCookieKey(key: 'hsSyncUserToken' | 'marketo') {
    // zngly telemetry integration
    return `zngly/tel:int:${key}`;
  }

  private hsSyncStop = false;
  private hsSyncRunning = false;

  public syncHubspot(newLeadInfo?: boolean) {
    if (this.hsSyncRunning) return;
    this.hsSyncRunning = true;

    if (!window?._hsq || !Array.isArray(window._hsq)) {
      window._hsq = [];
    }
    // in most cases -> it is better to wait before syncing the user token
    // if the user has just been identified, we have to let hubspot update their internal records
    // in order to get a match with the user token
    // also -> when called after cookie accepted -> take some time to let cookie be set and updated in document.cookie
    setTimeout(async () => {
      await this.syncHubspotCore(newLeadInfo);

      this.hsSyncRunning = false;
    }, 2000);
  }

  private async syncHubspotCore(_newLeadInfo?: boolean): Promise<void> {
    let newLeadInfo = _newLeadInfo;
    // check for _hsmi, _hsenc & utm_source=hs_email in the url params -> this means the visitor is coming from a hubspot marketing email
    const params = new URLSearchParams(window.location.search);
    const isHubspotMarketingEmail =
      params.get('utm_source') === 'hs_email' && !!params.get('_hsmi') && !!params.get('_hsenc');
    if (isHubspotMarketingEmail) newLeadInfo = true;

    // if the user has already been synced, do not sync again
    if (this.hsSyncStop === true && !newLeadInfo) return;

    type ZnglyHSCookies = {
      hubspotutk: string;
      visitorId: string;
    };

    const cookies = {
      hubspotutk: getCookie<string>('hubspotutk'),
      visitorId: Analytics.getClientId(),
    };

    if (!cookies.hubspotutk || !cookies.visitorId) return;

    const data = getCookie<ZnglyHSCookies>(this.getCookieKey('hsSyncUserToken'), true);

    if (data?.hubspotutk === cookies?.hubspotutk && data?.visitorId === cookies?.visitorId) {
      return;
    }

    const response = await hubspotUserTokenSyncMutationCompat({ ...(cookies as ZnglyHSCookies), newLeadInfo });
    if (response?.data?.hsSyncUserToken?.status === 'SUCCESS') {
      setCookie(this.getCookieKey('hsSyncUserToken'), JSON.stringify(cookies), { days: 10 });
      this.hsSyncStop = true;
    }
    if (response?.data?.hsSyncUserToken?.status === 'SESSION_ALREADY_SYNCED') this.hsSyncStop = true;
  }

  private marketoQueryRunning = false;
  public syncMarketo() {
    if (this.marketoQueryRunning === true) return;

    const mktoTrkCookie = getCookie<string>('_mkto_trk');
    if (!mktoTrkCookie) return;

    const visitorId = Analytics.getClientId();

    type ZnglyMarketoCookie = {
      _mkt_trk: string;
      mkt_tok?: string;
      visitorId: string;
    };

    let znMarketoCookie = getCookie<ZnglyMarketoCookie>(this.getCookieKey('marketo'), true);

    const url = new URL(window.location.href);
    const mktTokParam = url.searchParams.has('mkt_tok') ? url.searchParams.get('mkt_tok') : null;

    // logic for retrying the marketo sync
    let shouldContinue = false;

    // if the user is coming from a marketo email, we should sync
    if (
      !!mktTokParam &&
      (znMarketoCookie?.mkt_tok !== mktTokParam ||
        znMarketoCookie?.visitorId !== visitorId ||
        znMarketoCookie?._mkt_trk !== mktoTrkCookie)
    ) {
      deleteCookie(this.getCookieKey('marketo'));
      znMarketoCookie = null;
      shouldContinue = true;
    }

    if (shouldContinue === false) {
      return;
    }

    this.marketoQueryRunning = true;
    // if the user is coming from a marketo email, we will perform a sync to marketo
    // this can identify the user and track their activity without the need of a form submission
    setTimeout(
      () =>
        integrationsSyncMutation(
          {
            input: {
              visitorId,
              data: [
                mktTokParam
                  ? {
                      key: 'mkt_tok',
                      value: url.searchParams.get('mkt_tok') ?? '',
                    }
                  : null,
                {
                  key: '_mkto_trk',
                  value: mktoTrkCookie,
                },
              ],
            },
          },
          {
            onCompleted: (data, errors) => {
              this.marketoQueryRunning = false;
              if (!!errors?.length) return;

              const marketoData = data?.telemetryIntegrationsSync?.data?.find((x) => x?.key === 'marketo');
              if (!marketoData) return;

              setCookie(
                this.getCookieKey('marketo'),
                JSON.stringify({
                  _mkt_trk: mktoTrkCookie,
                  mkt_tok: mktTokParam,
                  visitorId,
                } as ZnglyMarketoCookie),
                {
                  days: 7,
                }
              );
            },
          }
        ),
      500
    );
  }
}

export { TelemetrySyncSession };
